import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import POS from './pages/POS';
import Clients from './pages/Clients';
import Appointments from './pages/Appointments';
import Inventory from './pages/Inventory';

import Users from './pages/UsersRoles';
import Settings from './pages/Settings';
import Finance from './pages/Finance';
import Layout from './components/Layout';

import ReportsSales from './pages/ReportsSales';
import ReportsInventory from './pages/ReportsInventory';
import ReportsFinancial from './pages/ReportsFinancial';
import ReportsClients from './pages/ReportsClients';
import ReportsSalesPrint from './pages/ReportsSalesPrint';
import ReportsInventoryPrint from './pages/ReportsInventoryPrint';
import ReportsFinancialPrint from './pages/ReportsFinancialPrint';
import ReportsClientsPrint from './pages/ReportsClientsPrint';
import Staff from './pages/Staff';
import Suppliers from './pages/Suppliers';
import AuditLog from './pages/AuditLog';
import DataImport from './pages/DataImport';
import HistoricalDataEntry from './pages/HistoricalDataEntry';
import PeriodManagement from './pages/PeriodManagement';
import HistoricalEntriesViewer from './pages/HistoricalEntriesViewer';

import { AuthProvider } from './context/AuthContext';
import { ThemeProvider as AppThemeProvider } from './context/ThemeContext';
import PrivateRoute from './components/PrivateRoute';
import RoleRoute from './components/RoleRoute';
import ClientDetails from './pages/ClientDetails';
import Payment from './pages/Payment';
import Credits from './pages/Credits';
import TransactionReceipt from './pages/TransactionReceipt';

// Create Material UI theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppThemeProvider>
        <Router>
          <ToastContainer position="top-right" autoClose={3000} />
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
              <Route index element={<Navigate to="/dashboard" />} />

              <Route path="dashboard" element={<Dashboard />} />
              <Route path="pos" element={<POS />} />
              <Route path="clients" element={<Clients />} />
              <Route path="clients/:id" element={<ClientDetails />} />
              <Route path="appointments" element={<Appointments />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="finance" element={<Finance />} />

              <Route path="reports/sales" element={<ReportsSales />} />
              <Route path="reports/inventory" element={<ReportsInventory />} />
              <Route path="reports/financial" element={<ReportsFinancial />} />
              <Route path="reports/clients" element={<ReportsClients />} />

              <Route path="users" element={(
                <RoleRoute allowedRoles={['admin']}>
                  <Users />
                </RoleRoute>
              )} />
              <Route path="settings" element={(
                <RoleRoute allowedRoles={['admin']}>
                  <Settings />
                </RoleRoute>
              )} />
              <Route path="payment" element={<Payment />} />
              <Route path="credits" element={<Credits />} />
              <Route path="staff" element={<Staff />} />
              <Route path="suppliers" element={<Suppliers />} />
              <Route path="audit-log" element={(
                <RoleRoute allowedRoles={['admin']}>
                  <AuditLog />
                </RoleRoute>
              )} />
              <Route path="import" element={(
                <RoleRoute allowedRoles={['admin']}>
                  <DataImport />
                </RoleRoute>
              )} />
              <Route path="historical-entry" element={(
                <RoleRoute allowedRoles={['admin']}>
                  <HistoricalDataEntry />
                </RoleRoute>
              )} />
              <Route path="periods" element={(
                <RoleRoute allowedRoles={['admin']}>
                  <PeriodManagement />
                </RoleRoute>
              )} />
              <Route path="historical-entries" element={(
                <RoleRoute allowedRoles={['admin']}>
                  <HistoricalEntriesViewer />
                </RoleRoute>
              )} />
              <Route path="finance/receipt/:receiptNumber" element={<TransactionReceipt />} />

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>

            {/* Print routes - standalone without layout */}
            <Route path="/reports/sales/print" element={<ReportsSalesPrint />} />
            <Route path="/reports/inventory/print" element={<ReportsInventoryPrint />} />
            <Route path="/reports/financial/print" element={<ReportsFinancialPrint />} />
            <Route path="/reports/clients/print" element={<ReportsClientsPrint />} />
          </Routes>
        </Router>
        </AppThemeProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;